import { useMemo } from 'react';
import { r, s } from '@/theme/tokens';
import { chartBarSizing } from '../theme/chartBarSizing';
import { Box } from './Box';
import { useTheme } from './hooks';
import { Skeleton } from './Skeleton';

type StackedBarChartSkeletonProps = {
  height?: number;
  bars?: number;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export const StackedBarChartSkeleton = ({
  height = 200,
  bars = 7,
}: StackedBarChartSkeletonProps): JSX.Element => {
  const categoryGapRatio = chartBarSizing.default.categoryGapRatio;
  const theme = useTheme();
  const axisLeft = `calc(${s('2xl')} + ${s('lg')})`;
  const barHeights = useMemo(() => {
    const resolvedBars = clamp(bars, 4, 18);
    return Array.from({ length: resolvedBars }, (_, index) => {
      const offset = Math.abs((index % 5) - 2);
      const multiplier = 0.4 + offset * 0.12;
      return clamp(height * multiplier, height * 0.35, height * 0.9);
    });
  }, [bars, height]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height }}>
      <Box
        sx={{
          position: 'absolute',
          left: axisLeft,
          right: s('xl'),
          bottom: s('2xl'),
          height: '1px',
          backgroundColor: theme.app.colors.divider,
          opacity: 0.7,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: axisLeft,
          top: s('md'),
          bottom: s('2xl'),
          width: '1px',
          backgroundColor: theme.app.colors.divider,
          opacity: 0.7,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: axisLeft,
          right: s('xl'),
          bottom: s('2xl'),
          top: s('md'),
          display: 'flex',
          alignItems: 'flex-end',
          gap: `calc(${s('3xl')} * ${categoryGapRatio / chartBarSizing.default.categoryGapRatio})`,
          paddingLeft: s('2xl'),
          paddingRight: s('2xl'),
        }}
      >
        {barHeights.map((barHeight, index) => (
          <Box
            key={`bar-${index}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              gap: s('xxxs'),
              flex: 1,
              minWidth: 0,
            }}
          >
            <Skeleton
              variant="rectangular"
              height={barHeight}
              sx={{
                borderRadius: `${r('md')} ${r('md')} ${r('none')} ${r('none')}`,
                bgcolor: theme.app.colors.border,
                opacity: 0.6,
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};
